import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Check, X, Star, ArrowSquareOut } from '@phosphor-icons/react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminDashboard() {
  const navigate = useNavigate();
  const { user, logout, token } = useAuth();
  const [masterclasses, setMasterclasses] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user) {
      fetchMasterclasses();
    }
  }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchMasterclasses = async () => {
    try {
      const response = await axios.get(`${API}/masterclasses?approved=false`);
      setMasterclasses(response.data);
    } catch {
      // silently handle fetch errors
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (id) => {
    try {
      await axios.patch(
        `${API}/masterclasses/${id}/approve`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Masterclass approved');
      fetchMasterclasses();
    } catch (error) {
      toast.error('Failed to approve masterclass');
    }
  };

  const handleReject = async (id) => {
    try {
      await axios.patch(
        `${API}/masterclasses/${id}/reject`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Masterclass rejected');
      fetchMasterclasses();
    } catch (error) {
      toast.error('Failed to reject masterclass');
    }
  };

  const handleFeature = async (id, featured) => {
    try {
      await axios.patch(
        `${API}/masterclasses/${id}/feature?featured=${featured}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(featured ? 'Masterclass featured' : 'Masterclass unfeatured');
      fetchMasterclasses();
    } catch (error) {
      toast.error('Failed to update feature status');
    }
  };

  return (
    <div className="min-h-screen bg-white text-gray-900">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 gradient-header">
        <div className="max-w-7xl mx-auto px-6 py-4 flex justify-between items-center">
          <div onClick={() => navigate('/')} className="text-2xl font-bold tracking-tight cursor-pointer text-white flex items-center gap-0" data-testid="logo">
            <img src="/logo.png" alt="NeoMaestro" className="h-9 w-9 object-contain bg-white rounded-lg" />
            <span>NeoMaestro</span>
          </div>
          <div className="flex gap-4 items-center">
            <span className="text-white/80">Admin: {user?.name}</span>
            <Button variant="ghost" onClick={logout} className="text-white/90 hover:text-white hover:bg-transparent" data-testid="logout-btn">
              Logout
            </Button>
          </div>
        </div>
      </nav>

      <div className="pt-24 px-6 pb-12">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="mb-12">
            <h1 className="text-5xl font-black tracking-tighter mb-4" data-testid="dashboard-title">Admin Dashboard</h1>
            <p className="text-xl text-gray-600">Review and manage masterclass submissions</p>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-12">
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-6">
              <div className="text-4xl font-black text-[#00a3af] mb-2" data-testid="pending-count">{masterclasses.length}</div>
              <div className="text-gray-600">Pending Approval</div>
            </div>
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-6">
              <Button
                onClick={() => navigate('/marketplace')}
                className="bg-[#00F0FF] text-black hover:bg-[#34c759] rounded-full"
                data-testid="view-live-btn"
              >
                View Live Masterclasses <ArrowSquareOut size={20} className="ml-2" />
              </Button>
            </div>
          </div>

          {/* Pending Masterclasses */}
          <div>
            <h2 className="text-3xl font-bold mb-6" data-testid="pending-title">Pending Masterclasses</h2>
            {loading ? (
              <div className="text-center py-12 text-gray-600">Loading...</div>
            ) : masterclasses.length === 0 ? (
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-12 text-center">
                <p className="text-gray-600" data-testid="no-pending-message">No pending masterclasses to review</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 gap-6">
                {masterclasses.map((mc, index) => (
                  <motion.div
                    key={mc.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.05 }}
                    className="bg-gray-50 border border-gray-200 rounded-lg p-8 hover:border-[#CCFF00] transition-colors"
                    data-testid={`masterclass-item-${index}`}
                  >
                    <div className="flex justify-between items-start mb-6">
                      <div className="flex-1">
                        <div className="text-xs uppercase tracking-wider text-[#00a3af] font-bold mb-2">{mc.category}</div>
                        <h3 className="text-2xl font-bold mb-2">{mc.title}</h3>
                        <p className="text-gray-600 mb-4">{mc.description}</p>
                        
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                          <div>
                            <div className="text-xs text-gray-600">Instructor</div>
                            <div className="font-bold">{mc.instructor_name}</div>
                          </div>
                          <div>
                            <div className="text-xs text-gray-600">Date</div>
                            <div className="font-bold">{mc.date}</div>
                          </div>
                          <div>
                            <div className="text-xs text-gray-600">Price</div>
                            <div className="font-bold">{mc.price}</div>
                          </div>
                          <div>
                            <div className="text-xs text-gray-600">Format</div>
                            <div className="font-bold capitalize">{mc.format}</div>
                          </div>
                        </div>

                        <div className="flex items-center gap-2">
                          <span className="text-xs text-gray-600">External Link:</span>
                          <a
                            href={mc.external_link}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-xs text-[#00a3af] hover:text-[#00a3af] underline"
                          >
                            {mc.external_link}
                          </a>
                        </div>
                      </div>
                    </div>

                    <div className="flex gap-4 border-t border-gray-200 pt-6">
                      <Button
                        onClick={() => handleApprove(mc.id)}
                        className="bg-[#34c759] text-black hover:bg-[#28a745] rounded-full flex-1"
                        data-testid={`approve-btn-${index}`}
                      >
                        <Check size={20} weight="bold" className="mr-2" /> Approve
                      </Button>
                      <Button
                        onClick={() => handleReject(mc.id)}
                        variant="outline"
                        className="border-gray-200 hover:border-red-500 hover:text-red-500 rounded-full flex-1"
                        data-testid={`reject-btn-${index}`}
                      >
                        <X size={20} weight="bold" className="mr-2" /> Reject
                      </Button>
                      <Button
                        onClick={() => handleFeature(mc.id, !mc.featured)}
                        variant="outline"
                        className="border-gray-200 hover:border-[#00F0FF] rounded-full"
                        data-testid={`feature-btn-${index}`}
                      >
                        <Star size={20} weight={mc.featured ? "fill" : "regular"} />
                      </Button>
                      <Button
                        onClick={() => window.open(`/masterclass/${mc.id}`, '_blank')}
                        variant="ghost"
                        className="text-gray-600 hover:text-[#00a3af] hover:bg-transparent"
                        data-testid={`view-btn-${index}`}
                      >
                        View <ArrowSquareOut size={16} className="ml-0.5" />
                      </Button>
                    </div>
                  </motion.div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}